import { autoPlacement, offset, ReferenceType } from "@floating-ui/react-dom";
import { useClick, useDismiss, useFloating, useInteractions } from "@floating-ui/react-dom-interactions";
import { Alert, Checkbox, Input } from "@material-tailwind/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { useContext, useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { Gallery } from "react-grid-gallery";
import SimpleBar from "simplebar-react";
import { useDebounce } from "use-debounce";
import loadImage from "utils/loadImg";
import resolveImgUrl from "utils/resolveImgUrl";
import Spinner from "./utils/Spinner";

type Prop = {
    postImageUrl : string,
    onSelect : (a : string)=>any | void,
    children : (reference : (node : ReferenceType | null)=>void, referenceProp : Record<string, unknown> )=>JSX.Element,
    strategy? : 'fixed' | 'absolute'
}

const fileTypes = ["JPEG", "JPG", "WEBP", "PNG", "GIF"];

const GalleryPicker = ({
    postImageUrl,
    onSelect,
    children,
    strategy : _strategy = 'absolute',
}:Prop)=>{
  
    const [open, setOpen] = useState(false);
    const [_search, setSearch] = useState('')
    const [search] = useDebounce(_search, 750);
    const [isUserPosted, setIsUserPosted] = useState<boolean>(true);

    const [fromGallery, setFromGallery] = useState<boolean>(false)
    const [file, setFile] = useState<File | null>(null);
    const [page, setPage] = useState(1);

    const queryClient = useQueryClient();
    const axiosPrivate = useAxiosPrivate();

    const handleSelect = (filename : string)=>{
      onSelect(filename);
      setSearch('');
      setPage(0);
      setOpen(false);
      setFromGallery(false)
    }

    const {data : gallery, status : galleryStatus, refetch : refetchGallery} = useQuery(['gallery', page, {search, limit:100, userPosted : isUserPosted}], async ()=>{
        const {data : _data} = await axiosPrivate.get(`/media/images/?page=${page}&limit=100&search=${search}&userPosted=${isUserPosted}`)
        const data = await Promise.all(
          _data[0].map(async(datum:any)=>{
            const filename = datum.file_name.split('.')
            const src = resolveImgUrl(datum.thumbnail_generated? filename[0] + '-thumb.' + filename[1]: datum.file_name)
            const {height, width} = await loadImage(src);
            return {
              src,width, height,
              alt : datum.original_name,
              key : datum.id,
              originalName : datum.file_name 
            }
          })
        )
        return data;
    },)

    useEffect(()=>{
      setPage(1);
      refetchGallery({});
    }, [search])

    const {x, y, reference, floating, strategy, context} = useFloating({
        open,
        onOpenChange: setOpen,
        strategy : _strategy,
        middleware: [offset(10), autoPlacement()]
    });
    const {getReferenceProps, getFloatingProps} = useInteractions([
      useClick(context),
      useDismiss(context),
    ]);

    useEffect(()=>{
      localStorage.setItem('pick-img-from-gallery', fromGallery + '');
    }, [fromGallery])

    function postImage(image : File){
      const data = new FormData();
      data.append('image', image, image.name)
      return axiosPrivate.post(postImageUrl, data)
    }
    const {status : postImgStatus, mutate : uploadImg, error} = useMutation(postImage, {
      onSuccess(data){
        queryClient.invalidateQueries(['gallery'])
        handleSelect(data.data);
        setOpen(false);
      },
      onSettled(){
        setFile(null);
      }
    })

    const handleChange = (file : File) => {
      uploadImg(file)
      setFile(file);
    };
    
    return <>
        {children(reference, getReferenceProps())}
        {( open && 
          <div
              ref={floating}
              className="z-10"
              style={{
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
              }}
              {...getFloatingProps()}
          >
            <div className="h-64 w-64 text-white flex flex-col">
              <div className="w-full grid grid-cols-2 h-8">
                <div 
                  onClick={()=>setFromGallery(false)}
                  className={(!fromGallery ? "bg-slate-900 text-slate-200 z-[5] pointer-events-none" : "text-slate-400 border-t border-l border-slate-400 bg-slate-100 translate-y-1 translate-x-1 cursor-pointer" ) 
                  + " grid rounded-tl-md place-content-center px-3 py-1 text-sm transition-transform"}
                >
                    Upload
                </div>
                <div 
                  onClick={()=>setFromGallery(true)}
                  className={(fromGallery ? "bg-slate-900 text-slate-200 z-[5] pointer-events-none" : "text-slate-400 border-t border-r border-slate-400 bg-slate-100 translate-y-1 -translate-x-1 cursor-pointer" ) 
                  + " grid rounded-tr-md place-content-center px-3 py-1 text-sm transition-transform"}
                >
                    From Gallery
                </div>
              </div>
              <div className="grow border border-slate-400 border-t-4 bg-white border-t-slate-900 shadow-lg relative z-10">
                {( !fromGallery ? 
                  <div className="w-full h-full text-slate-900 cursor-pointer relative">
                    { !file ?<>
                        {(postImgStatus === 'error' && 
                        <div className=" absolute top-0 left-0 w-full h-10 pointer-events-none">
                          <Alert className="text-sm" color="red">
                            {/* @ts-ignore */}
                            {error.response?.data?.statusCode === 400 ? 'Size must not exceed 5mb' : error.message}
                          </Alert>
                        </div>)}
                        <FileUploader classes="grid place-content-center !w-full !h-full !min-w-0 !max-w-none gallery-upload" handleChange={handleChange} name="file" types={fileTypes} />
                      </>
                      : <div className="w-full h-full grid place-content-center">
                        {postImgStatus === "loading" && <Spinner></Spinner>}
                      </div>
                    }
                  </div>
                  :
                  <div className="w-full h-full text-slate-900">
                    <div className="mx-2 my-2">
                      <Input placeholder="Search Image" value={_search} onChange={(e)=>setSearch(e.target.value)} icon={<i className="fas fa-magnifying-glass"></i>}/>
                      <div className="flex items-center">
                        <Checkbox 
                          onChange={(e)=>setIsUserPosted(e.target.checked)}
                          checked={isUserPosted}  
                          label="Only your image?"
                        />
                      </div>
                    </div>
                    <SimpleBar className="h-[11rem]">
                      <div className="">
                        {( galleryStatus==='loading' && <div className="w-full h-[11rem] relative grid place-content-center">
                          {postImgStatus === "loading" && <Spinner></Spinner>}
                        </div>)}
                        {( galleryStatus === 'success' && 
                          gallery.length > 0 ?
                            <Gallery images={gallery} onClick={(_, item)=>{handleSelect(item.originalName)}}/>
                            : <div className="w-full h-[11rem] grid place-content-center">
                              <div className="flex flex-col items-center">
                                <i className="fas fa-circle-question text-4xl"></i>
                                <div>No Image Found</div>
                              </div>
                            </div>
                        )}
                      </div>
                    </SimpleBar>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
    </>
}

export default GalleryPicker;